import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Account } from '@mkp/account/models';
import { CartDto } from '@mkp/publication/models';
import { Product, ScrollDirection } from '@mkp/shared/models';
import { AppConfig } from '@mkp/shared/util-app-config';
import {
  BaseEvent,
  ContentEvent,
  ContentTypeType,
  EcommerceActionType,
  EcommerceCreditUsedEvent,
  EcommerceEvent,
  EcommerceEventNameType,
  EcommercePlatformType,
  EventActionType,
  EventCategoryType,
  EventDetailsType,
  EventFunnelType,
  EventNameType,
  JobAdAtCreationEvent,
  JobAdDeleteDraftEvent,
  JobAdDuringCreationEvent,
  JobAdEvent,
  JobAdStopEvent,
  UserLoginMethodType,
} from '@mkp/tracking/models';
import { User } from '@mkp/user/models';
import { TranslateService } from '@ngx-translate/core';
import { getGlobalCategory } from './get-global-category';
import { getPageCategory, getSuffixedPageCategory } from './get-page-category';
import {
  EcommercePayload,
  getEcommerceCategory,
  getEcommerceCategoryFromProducts,
} from './helpers/get-ecommerce-category';
import { getEventCategory } from './helpers/get-event-category';
import { getFunnelStep } from './helpers/get-funnel-step';
import {
  getJobAdCategory,
  getJobAdStopCategory,
  JobAdPayload,
} from './helpers/get-job-ad-category';
import { getPromotionCategory } from './helpers/get-promotion-category';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly window = inject(Window);

  getPageViewEvent(): BaseEvent {
    return getEventCategory({
      name: 'page_view',
      category: 'user',
      action: 'user_navigation',
    });
  }

  // globals are pushed to the TealiumService by an effect, but login/signup/registration fire on
  // the very actions that change the user, and NgRx gives no ordering guarantee between those
  // effects, so these three build their globals from the payload instead.
  getLoginEvent({
    user,
    account,
    authMethod: user_auth_method,
    eventName,
    eventDetails: event_details,
  }: {
    user: User | null | undefined;
    account: Account | undefined;
    authMethod: UserLoginMethodType | undefined;
    eventName: Extract<EventNameType, 'user_login' | 'user_login_start'>;
    eventDetails?: Extract<EventDetailsType, 'mfa enabled' | 'mfa disabled'>;
  }): BaseEvent {
    return {
      ...getGlobalCategory(user, account),
      ...getEventCategory({
        name: eventName,
        category: 'user',
        action: eventName,
      }),
      ...(user_auth_method && { user_auth_method }),
      ...(event_details && { event_details }),
    };
  }

  getSignupEvent({
    user,
    account,
  }: {
    user: User | null | undefined;
    account: Account | undefined;
  }): BaseEvent {
    return {
      ...getGlobalCategory(user, account),
      ...getEventCategory({
        name: 'user_signup',
        category: 'user',
        action: 'user_signup',
      }),
      event_funnel_name: 'user_onboarding',
      event_funnel_step: 4,
    };
  }

  getJobAdBackButtonEvent(funnelStep: number): ContentEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      event_funnel_name: 'job_create',
      event_funnel_step: funnelStep,
      content_type: 'back_button',
    };
  }

  getJobAdSuggestButtonEvent({
    job_id,
    job_tmp_id,
  }: {
    job_id: string | undefined;
    job_tmp_id: string | undefined;
  }): JobAdDuringCreationEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      event_funnel_name: 'job_create',
      event_funnel_step: 1,
      content_type: 'suggest_button',
      job_id: job_id,
      job_tmp_id: job_tmp_id,
    };
  }

  getJobAdCancelSuggestButtonEvent({
    job_id,
    job_tmp_id,
  }: {
    job_id: string | undefined;
    job_tmp_id: string | undefined;
  }): JobAdDuringCreationEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      event_funnel_name: 'job_create',
      event_funnel_step: 1,
      content_type: 'stop_generate_button',
      job_id: job_id,
      job_tmp_id: job_tmp_id,
    };
  }

  getJobAdCreateStartEvent(): BaseEvent {
    return {
      ...getEventCategory({
        name: 'job_ad_create_start',
        category: 'job_ad',
        action: 'job_ad_create_start',
      }),
      event_funnel_name: 'job_create',
      event_funnel_step: 1,
    };
  }

  getJobAdDeleteDraftEvent(id: string): JobAdDeleteDraftEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      content_type: 'delete_draft',
      job_id: id,
    };
  }

  getJobAdCreatedEvent(jobAd: JobAdPayload): JobAdEvent {
    return {
      ...getEventCategory({
        name: 'job_ad_publish',
        category: 'job_ad',
        action: 'job_ad_publish',
      }),
      ...getJobAdCategory(jobAd),
      event_funnel_name: 'job_create',
      event_funnel_step: 4,
    };
  }

  getJobAdStopEvent(jobAd: JobAdPayload): JobAdStopEvent {
    return {
      ...getEventCategory({
        name: 'job_ad_deactivated',
        category: 'job_ad',
        action: 'job_ad_deactivated',
      }),
      ...getJobAdStopCategory(jobAd),
    };
  }

  getUnverifiedCheckoutEvent({
    jobAd,
    ecommerce,
  }: {
    jobAd: JobAdPayload;
    ecommerce: EcommercePayload;
  }): EcommerceEvent {
    // remove the ecommerce_action information for the unverified checkout event
    const { ecommerce_action: _, ...ecommerceCategory } = getEcommerceCategory(
      ecommerce,
      this._translateService
    );
    const eventCategory = getEventCategory({
      name: 'job_ad_place_order',
      action: 'job_ad_place_order',
      category: 'job_ad',
    });

    return {
      ...eventCategory,
      ...getJobAdCategory(jobAd),
      ...ecommerceCategory,
      event_funnel_name: 'job_create',
      event_funnel_step: 4,
    };
  }

  getJobAdCreateStepEvent({
    stepIndex,
    nbSuggestions,
    vacancyTemporaryId,
    vacancyId,
  }: {
    stepIndex: number;
    nbSuggestions: number;
    vacancyTemporaryId: string;
    vacancyId: string;
  }): JobAdAtCreationEvent {
    return {
      ...getEventCategory({
        name: 'job_ad_create_step',
        category: 'job_ad',
        action: 'job_ad_create_step',
      }),
      event_funnel_name: 'job_application_funnel',
      event_funnel_step: stepIndex,
      job_suggestions: nbSuggestions,
      job_tmp_id: vacancyTemporaryId,
      job_id: vacancyId,
    };
  }

  getUserRegistrationEvent(user: Pick<User, 'email' | 'id' | 'settings'>): BaseEvent {
    return {
      ...getGlobalCategory(user, undefined),
      ...getEventCategory({
        name: 'user_registration',
        category: 'user',
        action: 'user_registration',
      }),
      event_funnel_name: 'user_onboarding',
      event_funnel_step: 2,
    };
  }

  getEcommerceEvent({
    ecommerce,
    jobAd,
    promotionCart,
    eventName,
    pageSuffix = '',
  }: {
    ecommerce: EcommercePayload;
    jobAd: JobAdPayload | undefined;
    promotionCart: CartDto | undefined;
    eventName: EcommerceEventNameType;
    pageSuffix?: string;
  }): EcommerceEvent {
    const event = getEventCategory({
      name: eventName,
      category: 'ecommerce',
      action: eventName,
    });

    const { page_title, page_category } = getPageCategory({
      pageReferrer: '',
      windowRef: this.window,
    });
    const ecommerceCategory = getEcommerceCategory(ecommerce, this._translateService);

    return {
      ...event,
      ...getSuffixedPageCategory({ page_title, page_category }, pageSuffix),
      ...(jobAd ? getJobAdCategory(jobAd) : {}),
      ...(promotionCart ? getPromotionCategory(promotionCart) : {}),
      ...ecommerceCategory,
      event_funnel_name: ecommerceCategory.ecommerce_checkout_funnel_name,
      event_funnel_step: getFunnelStep({
        funnel: ecommerceCategory.ecommerce_checkout_funnel_name ?? 'ecommerce',
        action: eventName,
      }),
    };
  }

  getProductScrollEvent(direction: ScrollDirection): ContentEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      event_funnel_name: 'job_create',
      event_funnel_step: 3,
      content_type: `scroll_button_${direction}` as ContentTypeType,
    };
  }

  getContactCustomerSupportEvent(): ContentEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      content_type: `internal_link_zendesk_open`,
    };
  }

  getLogoGuidelinesEvent(): ContentEvent {
    // The create flow is /vacancy/new plus /vacancy/:id/publish. The id sits mid-path in the
    // second, so a single substring no longer covers both.
    const isCreationFlow =
      this._router.url.includes(`${AppConfig.routes.vacancy}/${AppConfig.routes.new}`) ||
      new RegExp(`${AppConfig.routes.vacancy}/[^/]+/${AppConfig.routes.publish}`).test(
        this._router.url
      );

    return {
      ...getEventCategory({
        name: 'click',
        category: 'interaction',
        action: 'click',
      }),
      event_funnel_name: isCreationFlow ? 'job_ad_create' : undefined,
      event_funnel_step: isCreationFlow ? 2 : undefined,
      content_type: `internal_link_logoguidelines`,
    };
  }

  getEcommerceStoreEvent(products: Product[]): EcommerceEvent {
    const funnel: EventFunnelType = 'ecommerce_credit_purchase';
    const action: EventActionType = 'ecommerce_product_list_view';
    const eventCategory: EventCategoryType = 'ecommerce';
    const ecommerceAction: EcommerceActionType = 'detail';

    const event = getEventCategory({ name: action, category: eventCategory, action });
    const ecommerceCategory = getEcommerceCategoryFromProducts(
      products,
      funnel,
      undefined,
      ecommerceAction,
      this._translateService
    );

    return {
      ...event,
      ...ecommerceCategory,
      event_funnel_name: funnel,
      event_funnel_step: getFunnelStep({ funnel, action }),
    };
  }

  getEcommerceCreditUsedEvent({
    products,
    platform,
    jobId,
  }: {
    products: Product[];
    platform: EcommercePlatformType | undefined;
    jobId: string | undefined;
  }): EcommerceCreditUsedEvent {
    const funnel: EventFunnelType = 'job_ad_create';
    const action: EventActionType = 'ecommerce_credit_used';
    const eventCategory: EventCategoryType = 'ecommerce';
    const ecommerceAction: EcommerceActionType = 'purchase';

    const event = getEventCategory({ name: action, category: eventCategory, action });
    const ecommerceCategory = getEcommerceCategoryFromProducts(
      products,
      funnel,
      platform,
      ecommerceAction,
      this._translateService
    );

    return {
      job_id: jobId,
      ...event,
      ...ecommerceCategory,
      event_funnel_name: funnel,
      event_funnel_step: getFunnelStep({ funnel, action }),
    };
  }

  getJobAdPublishedEvent(jobAd: JobAdPayload): JobAdEvent {
    const funnel: EventFunnelType = 'job_ad_create';
    const action: EventActionType = 'job_ad_publish';
    const eventCategory: EventCategoryType = 'job_ad';

    const event = getEventCategory({ name: action, category: eventCategory, action });

    return {
      ...event,
      ...getJobAdCategory(jobAd),
      event_funnel_name: funnel,
      event_funnel_step: getFunnelStep({ funnel, action }),
    };
  }

  getBoostButtonEvent(jobAd: JobAdPayload): JobAdEvent {
    return {
      ...getEventCategory({
        name: 'click',
        category: 'ecommerce',
        action: 'click',
      }),
      ...getJobAdCategory(jobAd),
      event_funnel_name: 'ecommerce_boost_purchase',
      event_funnel_step: 1,
    };
  }
}
